import { Container, delayedCall, dispatcher, IEvent, ingenuity, killDelayedCall, SpineBase } from "@quest/base-core";
import { ReelPanel, SlotConstants, SlotEventConstants, SymbolFactory } from "@quest/slot-core";
import { IGame } from "@quest/adapter-interface";
import { eventDispatchers } from "../../Constants/EventDispatchers";
import { HoldAndRespinModel } from "../Model/HoldAndRespinModel";
import { HoldAndReSpinReel } from "./HoldAndReSpinReel";
import { GameParserModel } from "../../Platform/GameParserModel";
import { SlotConstantsBehavior, SlotEventConstantsBehavior } from "@quest/murka-games-behavior-panther";
import { SoundConstantGame } from "../../Constants/SoundConstantGame";

export class HoldAndRespinReelPanelController extends Container {
  private readonly model: HoldAndRespinModel;
  private reels: Array<HoldAndReSpinReel>;
  protected stopPos: number[] = [];
  private initiated: boolean = false;
  private isSpining: boolean;
  private spinAll: boolean = false;
  private reelsStopped: boolean[];
  private stopSpinAll: boolean;
  private paused: boolean = false;
  private stopNow: boolean = false;
  protected reelPanel: ReelPanel;

  constructor(currentGame: IGame, model: HoldAndRespinModel) {
    super(model.json, ingenuity.currentGame);
    this.model = model;
    this.reels = [];
    this.reelsStopped = [];
    const symbolPool: SymbolFactory = new SymbolFactory(this.model);
    for (let i: number = 0; i < this.model.getNumReels(); i++) {
      this.reels.push(new HoldAndReSpinReel(i, this.model.getSymPerReel(i), this.model, symbolPool));
      this.addChild(this.reels[i]);
    }
    this.subscribeEvents();
    this.showReels();
  }

  /**
   * subscribe all events
   */
  private subscribeEvents(): void {
    this.unsubscribeEvents();
    dispatcher.on(eventDispatchers.START_TV_REEL_SPINNING, this.featureReelSpin, this);
    dispatcher.on(eventDispatchers.STOP_TV_REEL, this.stop, this);
    dispatcher.on(eventDispatchers.UPDATE_REEL_SYMBOL, this.updateReelWithNewSymbol, this);
    dispatcher.on(eventDispatchers.HIDE_REEL_BEHIND_FISH, this.hideReelBehindBigFish, this);
    dispatcher.on(eventDispatchers.SHOW_REEL_BEHIND_FISH, this.showReelBehindBigFish, this);
  }
  /**
   * unsubscribe all events
  */
  public unsubscribeEvents(): void {
    for (let i: number = 0; i < this.model.getNumReels(); i++) {
      dispatcher.off(SlotEventConstants.REEL_MASK_ON, this.reels[i].reelMaskOn, this.reels[i]);
    }
    dispatcher.off(eventDispatchers.START_TV_REEL_SPINNING, this.featureReelSpin, this);
    dispatcher.off(eventDispatchers.STOP_TV_REEL, this.stop, this);
    dispatcher.off(eventDispatchers.UPDATE_REEL_SYMBOL, this.updateReelWithNewSymbol, this);
    dispatcher.off(eventDispatchers.HIDE_REEL_BEHIND_FISH, this.hideReelBehindBigFish, this);
    dispatcher.off(eventDispatchers.SHOW_REEL_BEHIND_FISH, this.showReelBehindBigFish, this);
  }
  /**
   * Initialize reels
   */
  public init(stops: any): void {
    for (let i: number = 0; i < this.model.getNumReels(); i++) {
      if (!Array.isArray(stops)) {
        this.reels[i].init(stops);
      } else {
        this.reels[i].init(stops[i]);
      }
      dispatcher.on(SlotEventConstants.REEL_MASK_ON, this.reels[i].reelMaskOn, this.reels[i]);

      if (!this.reels[i].hasEvent(eventDispatchers.FEATURE_REEL_STOPPED)) {
        this.reels[i].on(eventDispatchers.FEATURE_REEL_STOPPED, this.reelStopped, this, false);
      }
      if (!this.reels[i].hasEvent(eventDispatchers.FEATURE_REEL_STOPPING)) {
        this.reels[i].on(eventDispatchers.FEATURE_REEL_STOPPING, this.onReelStopping, this, false);
      }
    }
    this.initiated = true;
  }
  /**
   * Show the Reels
   */
  private showReels(): void {
    this.visible = true;
    for (let i: number = 0; i < this.model.getNumReels(); i++) {
      this.reels[i].visible = true;
    }
  }
  /**
   * Hide the Reels
   */
  private hideReels(reelId?: number): void {
    this.visible = false;
    for (let i: number = 0; i < this.model.getNumReels(); i++) {
      this.reels[i].visible = false;
    }
  }

  /**
   * Start spining
   */
  private featureReelSpin(): void {
    const isBlur: boolean = false;
    if (!this.initiated) {
      throw new Error("Can not Spin, Reels Not initiated");
    }
    dispatcher.fireEvent(SlotEventConstants.CLEAR_ANIM_LAYER);
    let i: number = 0;
    if (this.model.noStartDelay) {
      for (i = 0; i < this.reels.length; i++) {
        this.reels[i].spin(isBlur);
      }
      this.isSpining = true;
    }
    this.stopSpinAll = false;
    this.spinAll = true;
    this.reelsStopped.length = 0;
    ingenuity.currentGame.ticker.add(this.update, this);
  }
  /**
   * while spining update will call
   */
  public update() {
    super.update.call(this);
    if (this.paused) {
      return;
    }
    if (!this.spinAll) {
      return;
    }
    let len: number = this.reels.length,
      i;
    let deltaTime: number = ingenuity.currentGame.time.elapsedMS;
    for (i = 0; i < len; i++) {
      this.reels[i].updateSpin(deltaTime);
    }
    if (this.stopSpinAll) {
      this.spinAll = false;
      this.stopSpinAll = false;
      this.isSpining = false;
      ingenuity.currentGame.ticker.remove(this.update, this);
    }
  }
  /**
   * start stoping reels
   */
  private stop(evt: any) {
    let stops = evt.data;
    killDelayedCall(SlotConstants.StopSpinTimer);
    if (!this.isSpining) {
      delayedCall(SlotConstants.StopSpinTimer, 100, this.stop.bind(this, stops), this, stops);
      return;
    }
    let i: number = 0;
    if (Array.isArray(stops)) {
      this.model.reelStops = stops;
    } else if (typeof stops === "object" && stops && stops.data) {
      if (Array.isArray(stops.data)) {
        this.model.reelStops = stops.data;
      } else {
        throw new Error("No response recived or response not in the form of an Array" + stops.data);
      }
    } else {
      throw new Error("No response recived or response not in the form of an Array" + stops);
    }

    if (this.model.noStopDelay) {
      for (i = 0; i < this.reels.length; i++) {
        this.reels[i].setStops(stops[i]);
      }
    } else if (this.model.reelsJson[i].noDelay) {
      this.stopReel.bind(this, i, true);
    } else {
      let delay: number = Array.isArray(this.model.stopDelay) ? this.model.stopDelay[i] : this.model.stopDelay;
      delayedCall(SlotConstants.StopReelId + i, delay, this.stopReel.bind(this, i, true));
    }
  }

  /**
   * stop reel
   */
  protected stopReel(id: number, allReelSpining: boolean) {
    if (!allReelSpining) {
      return;
    }
    if (this.reels[id].getIsSpining() && !this.paused) {
      if (this.model.reelStops.length) {
        this.reels[id].setStops(this.model.reelStops[id]);
      } else {
        this.reels[id].setStops(this.model.reelGrid[id]);
      }
      id++;
      if (id < this.reels.length) {
        let delay: number = Array.isArray(this.model.stopDelay) ? this.model.stopDelay[id] : this.model.stopDelay;
        if (this.model.anticipationSpin[id]) {
          delay = this.model.getAnticipationDelay(id);
        }
        if (this.model.reelsJson[id].noDelay) {
          this.stopReel.bind(this, id, this.isSpining);
        } else if (ingenuity.configData.stopNowWithDelay && this.stopNow) {
          delayedCall(
            SlotConstants.StopReelId + id,
            ingenuity.configData.stopNowWithDelay,
            this.stopReel.bind(this, id, this.isSpining)
          );
        } else {
          delayedCall(SlotConstants.StopReelId + id, delay, this.stopReel.bind(this, id, this.isSpining));
        }
      }
    } else {
      if (ingenuity.configData.stopNowWithDelay && this.stopNow) {
        delayedCall(
          SlotConstants.StopReelId + id,
          ingenuity.configData.stopNowWithDelay / 2,
          this.stopReel.bind(this, id, this.isSpining)
        );
      } else {
        let delay: number = Array.isArray(this.model.stopDelay) ? this.model.stopDelay[id] : this.model.stopDelay;
        delayedCall(SlotConstants.StopReelId + id, delay / 4, this.stopReel.bind(this, id, this.isSpining));
      }
    }
  }

  /**
   * This function is called when the reel's stop bounce starts and this is triggered from the reel and is fired from the reelpanel.
   */
  protected onReelStopping(evt?: any) {
    if (dispatcher.hasEvent(eventDispatchers.FEATURE_REEL_STOPPING)) {
      dispatcher.fireEvent(eventDispatchers.FEATURE_REEL_STOPPING, evt);
    }
  }

  /**
   * All reels stoped
   */
  protected reelStopped(evt: any) {
    this.reelsStopped.push(true);
    dispatcher.fireEvent(eventDispatchers.SHOW_SYMBOL_ON_REEL, evt);

    if (this.reelsStopped.length >= this.reels.length - this.model.getTotalStoppedReels()) {
      this.stopSpinAll = true;
      this.reelsStopped.length = 0;
      this.reelsStopped = [];
      if ((ingenuity.parserModel as GameParserModel).getIsSpinsReset()) {
        dispatcher.fireEvent(eventDispatchers.RESET_COUNTER_TO_MAX);
      }
      dispatcher.fireEvent(SlotEventConstantsBehavior.STOP_GAME_SOUND, { id: SoundConstantGame.LAST_REEL_SOUND_RESPIN, key: SlotConstantsBehavior.SOUND_LIST_POSTLOAD });
      delayedCall("allReelStopped", 800, () => {
        killDelayedCall("allReelStopped");
        this.updateReelSpinningStatus();
        dispatcher.fireEvent(eventDispatchers.SEND_NEXT_SPIN_FLOW);
      },
        this
      );
    }
  }
  /**
   * to turn off spinning of all reels
   */
  private updateReelSpinningStatus(): void {
    for (let i: number = 0; i < this.model.getNumReels(); i++) {
      this.reels[i].stopAllReelSpinning();
    }
  }
  /**
/**
 * Returns a symbol from a reel at particular position.
 * @param reel
 * @param pos
 * @returns {StaticSymbol}
 */
  public getSymbolByPos(reel: number, pos: number) {
    return this.reels[reel].getSymbolByPos(pos);
  }
  protected updateReelWithNewSymbol(evt: IEvent) {
    const { symbolNumber, reel } = evt.data;
    this.reels[reel].init(this.model.getReelsets()[0][reel].indexOf(symbolNumber + 1));
  }
  protected hideReelBehindBigFish(evt: IEvent) {
    this.reels[evt.data].visible = false;
    (this.getSymbolByPos(evt.data, 0).getAnimation() as SpineBase).setPause(true)
  }
  protected showReelBehindBigFish(evt: IEvent) {
    this.reels[evt.data].visible = true;
  }

  /**
   * to reset reelpanel and it's data
   */
  public resetRespinReelPanel(): void {
    this.unsubscribeEvents();
    this.hideReels();
    this.reels = [];
    this.reelsStopped = [];
  }
}
